/**
 * #614 — the L1 app shell's menu bar (plan §7 L1, PRQ-08.2). Every app gets the same standard
 * menus in the same order — [App name] · Data · the app's own menus (≤ 4) · Requests · Help — and
 * the app's module declaration (GET /api/apps) fills them. The standard order and names are a
 * platform constant: a declaration may add items and its own menus, but it can never rename,
 * reorder or remove a standard menu.
 */
import { useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumb, Button, ButtonLink, DisabledWhy, L1Bar, Menu, MenuBarIntent, MenuColumns, MenuGroup, MenuItem, MenuText, menuColumns } from '@/kit';
import { appOpenPath, type MenuDecl, type PublishedApp } from '@/features/apps/apps-api';
import { useQuickLinkStar } from '@/features/quick-links/QuickLinks';
import { useMeta } from '@/app/meta';
import { logoPair } from '@/components/BrandLogo';

/** The platform's standard menus, in order. `app` sits first and takes the app's name. */
export const STANDARD_HEAD = [{ id: 'app' }, { id: 'data', label: 'Data' }] as const;
export const STANDARD_TAIL = [
  { id: 'requests', label: 'Requests' },
  { id: 'help', label: 'Help' },
] as const;
export const MAX_APP_MENUS = 4;

/**
 * The menu bar as it renders: the standard menus in the platform's order under the platform's
 * names (the app menu under the app's name), the declaration's own menus between Data and
 * Requests in declared order, at most four. A standard menu the declaration left out is still
 * there (with no items); a standard id declared as an app menu is treated as the standard one.
 */
export function resolveMenuBar(app: Pick<PublishedApp, 'name' | 'menuBar'>): MenuDecl[] {
  const standardIds = new Set<string>([...STANDARD_HEAD, ...STANDARD_TAIL].map((m) => m.id));
  const declared = new Map(app.menuBar.filter((m) => standardIds.has(m.id)).map((m) => [m.id, m]));
  const standard = (id: string, label: string): MenuDecl => ({ id, label, kind: 'standard', items: declared.get(id)?.items ?? [] });
  const own = app.menuBar.filter((m) => !standardIds.has(m.id)).slice(0, MAX_APP_MENUS).map((m) => ({ ...m, kind: 'app' as const }));
  return [
    standard('app', app.name),
    standard('data', 'Data'),
    ...own,
    ...STANDARD_TAIL.map((m) => standard(m.id, m.label)),
  ];
}

/**
 * QA11-P1-13: the one menu that holds the current page — the item whose route matches the location
 * best (longest path; a matching query wins a tie, so Data › Documents beats Help › Guide on the
 * Library opened for the app). Never two menus at once.
 */
export function activeMenuId(menus: readonly MenuDecl[], pathname: string, search = ''): string | null {
  return activeEntry(menus, pathname, search)?.menu ?? null;
}

/** R-16 T-135: the one item the current page is (aria-current="page" in its menu), by the same best match. */
export function activeItemId(menus: readonly MenuDecl[], pathname: string, search = ''): string | null {
  return activeEntry(menus, pathname, search)?.item ?? null;
}

function activeEntry(menus: readonly MenuDecl[], pathname: string, search: string): { menu: string; item: string } | null {
  const here = new URLSearchParams(search);
  let best: { menu: string; item: string; score: number } | null = null;
  for (const menu of menus) {
    for (const i of menu.items) {
      const [r = '', q = ''] = i.route.split('#')[0]!.split('?');
      if (r.length <= 1 || !(pathname === r || pathname.startsWith(`${r}/`))) continue;
      const params = [...new URLSearchParams(q)];
      if (params.some(([k, v]) => here.get(k) !== v)) continue;
      const score = r.length * 10 + params.length;
      if (!best || score > best.score) best = { menu: menu.id, item: i.id, score };
    }
  }
  return best;
}

/**
 * R-16 T-135 (design v1.2 change 02 §3): a menu's items as grouped columns (≤ 4 items: a plain list), each with its hint,
 * the current page marked, and the quick-link star; "Nothing here for your role yet." when the role has none.
 */
export function MegaItems({ menu, current }: { menu: MenuDecl; current: string | null }) {
  const star = useQuickLinkStar();
  const whyId = `star-why-${menu.id}`;
  if (menu.items.length === 0) return <MenuText>Nothing here for your role yet.</MenuText>;
  const cols = menuColumns(menu.items);
  return (
    <>
    <MenuColumns count={cols.length}>
      {cols.map((c) => (
        <MenuGroup key={c.title ?? '-'} title={c.title}>
          {c.items.map((item) => (
            <MenuItem
              key={item.id}
              render={<Link to={item.route} />}
              hint={item.hint}
              current={item.id === current}
              data-testid={`menu-item-${item.id}`}
              star={star ? { on: star.has(item.id), label: item.label, onToggle: () => star.toggle(item.id), disabledWhy: star.locked ? whyId : undefined } : undefined}
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuGroup>
      ))}
    </MenuColumns>
    {/* T-088: while viewing as, the stars are disabled and say why (the administrator's own list is kept) */}
    {star?.locked ? <DisabledWhy id={whyId}>{star.locked}</DisabledWhy> : null}
    </>
  );
}

/**
 * `menus`: the bar already resolved and cut to the user's tier (R-14 T-070); else the full bar.
 * R-15 T-107: an entry the app lists in `homeLinks` (GET /api/apps) reads as a link to `homeRoute`, the
 * landing page, with its other items behind a chevron; nothing about which entries is compiled in.
 */
export function AppMenuBar({ app, menus: given }: { app: Pick<PublishedApp, 'name' | 'menuBar' | 'slug' | 'homeRoute' | 'homeLinks'>; menus?: MenuDecl[] }) {
  const { pathname, search } = useLocation();
  const { logos } = useMeta();
  const menus = given ?? resolveMenuBar(app);
  const active = activeMenuId(menus, pathname, search);
  const current = activeItemId(menus, pathname, search);
  const mark = logoPair(logos, 'logomark');
  const tail = new Set<string>(STANDARD_TAIL.map((m) => m.id));
  const home = app.homeRoute ?? appOpenPath(app);
  const homeLinks = new Set(app.homeLinks ?? []);
  const items = (menu: MenuDecl) => <MegaItems menu={menu} current={current} />;
  const caret = (menu: MenuDecl, testId: string, hoverRef?: React.RefObject<HTMLElement | null>) => (
    <Menu
      hover
      mega
      hoverRef={hoverRef}
      aria-label={menu.label}
      trigger={
        <Button variant="quiet" icon aria-label={`More in ${menu.label}`} data-testid={testId} data-kind={menu.kind} className="k-l1-item k-l1-caret">
          <ChevronDown aria-hidden />
        </Button>
      }
    >
      {items(menu)}
    </Menu>
  );
  const trigger = (menu: MenuDecl) => {
    if (menu.id === 'app') {
      const name = (
        <>
          {/* #727: the CORE logomark beside the app's name (an uploaded logo; none is compiled in) */}
          {mark ? (
            <>
              <img src={mark.light} alt="" aria-hidden className="[.dark_&]:hidden" />
              <img src={mark.dark} alt="" aria-hidden className="hidden [.dark_&]:block" />
            </>
          ) : null}
          <span data-testid="app-name">{menu.label}</span>
        </>
      );
      return (
        <span key={menu.id} className="k-l1-split k-l1-home">
          {homeLinks.has('app') ? (
            <HomeSplit home={home} testId="app-home" className="k-l1-item k-l1-app" label={name} menu={menu.items.length ? (ref) => caret(menu, 'app-menu-app', ref) : null} />
          ) : (
            <Menu
              hover
              mega
              aria-label={menu.label}
              trigger={
                <Button variant="quiet" data-testid="app-menu-app" data-kind={menu.kind} className="k-l1-item k-l1-app">
                  {name}
                  <ChevronDown aria-hidden />
                </Button>
              }
            >
              {items(menu)}
            </Menu>
          )}
        </span>
      );
    }
    if (homeLinks.has(menu.id))
      return (
        <span key={menu.id} className="k-l1-split">
          <HomeSplit home={home} testId={`app-menu-${menu.id}-home`} kind={menu.kind} current={menu.id === active} className="k-l1-item" label={menu.label} menu={(ref) => caret(menu, `app-menu-${menu.id}`, ref)} />
        </span>
      );
    return (
      <Menu
        key={menu.id}
        hover
        mega
        align={tail.has(menu.id) ? 'end' : 'start'}
        aria-label={menu.label}
        trigger={
          <Button variant="quiet" data-testid={`app-menu-${menu.id}`} data-kind={menu.kind} aria-current={menu.id === active ? 'page' : undefined} className="k-l1-item">
            {menu.label}
          </Button>
        }
      >
        {items(menu)}
      </Menu>
    );
  };
  return (
    // R-16 T-135: one bar of hover menus — one open at a time, the next one opens at once once one is open
    <MenuBarIntent>
      <L1Bar
        label={`${app.name} menu`}
        app={app.slug}
        lead={menus.filter((m) => !tail.has(m.id)).map(trigger)}
        tail={menus.filter((m) => tail.has(m.id)).map(trigger)}
      />
    </MenuBarIntent>
  );
}

/** A label that returns to the landing page, beside its menu's chevron; hovering the label opens the menu too (v1.2). */
function HomeSplit({ home, testId, kind, current, className, label, menu }: { home: string; testId: string; kind?: string; current?: boolean; className: string; label: React.ReactNode; menu: ((ref: React.RefObject<HTMLElement | null>) => React.ReactNode) | null }) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  return (
    <>
      <ButtonLink ref={ref} to={home} variant="quiet" data-testid={testId} data-kind={kind} aria-current={current ? 'page' : undefined} className={className}>
        {label}
      </ButtonLink>
      {menu ? menu(ref) : null}
    </>
  );
}

/** The breadcrumb under the app bar: "<platform> › <app> › <menu>" in meta type, inside the gutter. */
export function AppCrumb({ app, menus: given }: { app: Pick<PublishedApp, 'name' | 'menuBar'>; menus?: MenuDecl[] }) {
  const { pathname, search } = useLocation();
  const { platformName } = useMeta();
  const menus = given ?? resolveMenuBar(app);
  const active = activeMenuId(menus, pathname, search);
  const here = menus.find((m) => m.id === active && m.id !== 'app');
  return (
    <Breadcrumb>
      {platformName ? (
        <>
          <b>{platformName}</b> ›{' '}
        </>
      ) : null}
      {app.name}
      {here ? ` › ${here.label}` : null}
    </Breadcrumb>
  );
}
